import React, { useEffect, useState } from 'react';
import FAQService from '../../Services/FAQ';
import PropertiesService from '../../Services/PropertiesService';
import Footer from '../Footer/Footer';
import './FAQ.css';
import { ChevronDown, ChevronUp } from 'lucide-react';

const DEFAULT_FAQS = [
  {
    message: "What does DoCity actually check?",
    answer: "A qualified civil engineer reviews your property's key documents: the sale deed (the legal record of the sale), the ownership chain (the history of who has owned it), and the land-use rules (what the land is allowed to be used for). We check these against your city's master plan and report back with a clear verdict and a market valuation."
  },
  {
    message: "Do I need this if I am just buying a normal flat or plot?",
    answer: "Yes. Problems like a disputed title or a planning violation are not visible to an ordinary buyer, and they are expensive to fix after you have paid. A verification is the cheapest protection you can get before you commit."
  },
  {
    message: "What is the difference between your three services?",
    answer: "DCP (DoCity Customer Property review) is for buyers checking a property before purchase. SOC (Self-Occupied Certificate) is for current owners who want their property documented with an ownership certificate. KYP (Know Your Property) is for current owners who want to understand their land use, value, and master-plan position."
  },
  {
    message: "Is the DoCity Seal a government certificate?",
    answer: "No. The DoCity Seal is our own certificate confirming that a qualified civil engineer has reviewed the property's documents and found them in order. It is a professional assessment you can keep and share, not a government guarantee of title."
  },
  {
    message: "How much does it cost, and how long does it take?",
    answer: "You will see the exact fee before you pay, after you submit your property details. Most reviews are completed within a few working days."
  },
  {
    message: "Can I talk to the engineer handling my property?",
    answer: "Yes. You are told what is happening at every stage, and you can reach your assigned engineer through the DoCity portal throughout the process."
  }
];

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phoneNumber: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState('');
  const [FAQsList] = useState(DEFAULT_FAQS);

  const toggleFAQ = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) newErrors.email = 'Please enter a valid email address.';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phoneNumber.trim())) newErrors.phoneNumber = 'Must be 10 digits';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name } = e.target;
    let value = e.target.value;

    if (name === 'phoneNumber') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }

    if (name === 'email') {
      value = value.replace(/\s/g, '');
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const response = await FAQService.createFaq(formData);
    console.log(response, 'response')
    if (response.message === 'Success') {
      setFormData({ name: '', email: '', phoneNumber: '', message: '' });
      setSubmitMessage('Your request has been submitted successfully!');
      setTimeout(() => {
        setSubmitMessage('')
      }, 2000)
    } else {
      setTimeout(() => {
        setSubmitMessage('')
      }, 2000)
      setErrors((prev) => ({ ...prev, errors: 'Something went wrong. Please try again.' }));
    }
  };

  return (
    <>
      <div className="ds-marketing faq-page">
        <div className="faq-section">
          <h2 className="faq-title">FAQs</h2>
          <p className="faq-subtitle">Answers to the questions people ask us most.</p>
          <div className="faq-list">
            {FAQsList.length === 0 ? (
              <div className="faq-empty-state">
                <div className="faq-empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <h3 className="faq-empty-title">No FAQs Available Yet</h3>
                <p className="faq-empty-desc">Use the form below to send us your question directly.</p>
              </div>
            ) : (
              FAQsList.map((faq, i) => (
                <div className={`faq-item ${activeIndex === i ? 'active' : ''}`} key={i}>
                  {/* aria-expanded / aria-controls so the accordion state is
                      announced; the chevron alone conveyed nothing to a screen
                      reader. */}
                  <button
                    type="button"
                    className="faq-question"
                    onClick={() => toggleFAQ(i)}
                    aria-expanded={activeIndex === i}
                    aria-controls={`faq-answer-${i}`}
                    id={`faq-question-${i}`}
                  >
                    <span className="faq-question-text">{faq.message}</span>
                    <span className="faq-chevron" aria-hidden="true">
                      {activeIndex === i ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </span>
                  </button>
                  {activeIndex === i && (
                    <div
                      className="faq-answer"
                      id={`faq-answer-${i}`}
                      role="region"
                      aria-labelledby={`faq-question-${i}`}
                    >
                      <p>{faq.answer || 'No detailed answer available.'}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        <div className="form-section">
          <h2 className="form-header">Get personalised assistance</h2>
          <p className="form-subtitle">
            Send us your question and our team will get back to you.
          </p>
          {/* Every label is now tied to its input with htmlFor/id — they were
              bare <label>s, so clicking one did not focus the field and screen
              readers announced the inputs unlabelled. */}
          <form className="verification-form" onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="faq-name">
                Full name <span className="ds-label__required">*</span>
              </label>
              <input
                id="faq-name"
                name="name"
                autoComplete="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                aria-invalid={Boolean(errors.name)}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="faq-email">
                Email address <span className="ds-label__required">*</span>
              </label>
              <input
                id="faq-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                aria-invalid={Boolean(errors.email)}
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="faq-phone">
                Contact number <span className="ds-label__required">*</span>
              </label>
              <input
                id="faq-phone"
                name="phoneNumber"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="10-digit mobile number"
                value={formData.phoneNumber}
                onChange={handleChange}
                aria-invalid={Boolean(errors.phoneNumber)}
              />
              {errors.phoneNumber && <span className="error-text">{errors.phoneNumber}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="faq-message">
                How can we help you? <span className="ds-label__required">*</span>
              </label>
              <textarea
                id="faq-message"
                name="message"
                placeholder="Enter your question or message here"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                aria-invalid={Boolean(errors.message)}
              ></textarea>
              {errors.message && <span className="error-text">{errors.message}</span>}
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">Send your inquiry</button>
              {errors.errors && <span className="error-text">{errors.errors}</span>}
              {/* aria-live so a successful submit is announced, not just shown. */}
              {submitMessage && (
                <p className="success-message" role="status" aria-live="polite">
                  {submitMessage}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FAQ;

